import { useState } from "react";
import { X, Eye, EyeOff, Save } from "lucide-react";
import { RichTextEditor } from "@/components/Editor/RichTextEditor";
import type {
  CreateReleaseNotePayload,
  ReleaseNoteAudience,
} from "@/services/release-notes.service";
import {
  checkRichTextLength,
  getRichTextLimitError,
} from "@/utils/richText";

interface ReleaseNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateReleaseNotePayload) => Promise<void> | void;
  isSubmitting?: boolean;
}

export function ReleaseNoteModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: ReleaseNoteModalProps) {
  const [title, setTitle] = useState("");
  const [audience, setAudience] = useState<ReleaseNoteAudience[]>([]);
  const [isAudienceOpen, setIsAudienceOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const { isOver: overLimit } = checkRichTextLength(description, 2000);

  // Convert YouTube links to embeds (mirrors ResourceSharing behavior)
  const processYouTubeContent = (content: string): string => {
    if (!content) return content;
    let processedContent = content.replace(
      /<a[^>]*href="(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+))"[^>]*>.*?<\/a>/g,
      (_match, _url, videoId) => {
        const embedUrl = `https://www.youtube.com/embed/${videoId}`;
        return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
      }
    );

    processedContent = processedContent.replace(
      /(?<!href=["'])https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?!["'])/g,
      (_match, videoId) => {
        const embedUrl = `https://www.youtube.com/embed/${videoId}`;
        return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
      }
    );

    processedContent = processedContent.replace(
      /<iframe([^>]*youtube[^>]*)><\/iframe>/g,
      '<div class="youtube-embed-wrapper"><iframe$1></iframe></div>'
    );

    return processedContent;
  };

  const audienceOptions: { label: string; value: ReleaseNoteAudience }[] = [
    { label: "Brand", value: "BRAND" },
    { label: "Agency", value: "AGENCY" },
  ];

  const toggleAudience = (value: ReleaseNoteAudience) => {
    setAudience((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !title.trim() ||
      audience.length === 0 ||
      !description.trim() ||
      overLimit
    )
      return;
    const processed = processYouTubeContent(description);
    await onSubmit({
      title: title.trim(),
      targetAudience: audience,
      description: processed,
    });
    setTitle("");
    setAudience([]);
    setDescription("");
    setShowPreview(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-xl border border-border max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            Create Release Note
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-muted-foreground"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto">
          {!showPreview ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What changed?"
                  className="mt-1 w-full rounded-lg border border-border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
                  maxLength={200}
                  required
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-muted-foreground">
                  Target Audience
                </label>
                <button
                  type="button"
                  onClick={() => setIsAudienceOpen((o) => !o)}
                  className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-between"
                >
                  <span className="text-muted-foreground">
                    {audience.length === 0
                      ? "Select audiences"
                      : `${audience.length} selected`}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      isAudienceOpen ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {isAudienceOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-lg">
                    <ul className="py-2 max-h-48 overflow-auto">
                      {audienceOptions.map((opt) => {
                        const checked = audience.includes(opt.value);
                        return (
                          <li
                            key={opt.value}
                            className="px-3 py-2 hover:bg-accent cursor-pointer flex items-center gap-2"
                            onClick={() => toggleAudience(opt.value)}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              className="rounded border-border text-[#d7785b] focus:ring-[#d7785b]"
                            />
                            <span className="text-muted-foreground">{opt.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Live selection preview */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {audience.length === 0 ? (
                    <span className="text-xs text-muted-foreground">
                      No audience selected
                    </span>
                  ) : (
                    audience
                      .map(
                        (v) =>
                          audienceOptions.find((o) => o.value === v)?.label || v
                      )
                      .map((label) => (
                        <span
                          key={label}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-muted text-muted-foreground border border-border"
                        >
                          {label}
                        </span>
                      ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Description
                </label>
                <RichTextEditor
                  content={description}
                  onChange={setDescription}
                  maxLength={2000}
                />
                {overLimit && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {getRichTextLimitError(description, 2000)}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-muted-foreground bg-muted rounded-lg hover:bg-accent"
                >
                  <Eye size={16} /> Preview
                </button>
                <button
                  type="submit"
                  disabled={
                    !title.trim() ||
                    !audience ||
                    !description.trim() ||
                    overLimit ||
                    isSubmitting
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#d7785b] text-white rounded-lg disabled:opacity-60 hover:bg-[#c66a4f]"
                >
                  <Save size={16} /> {isSubmitting ? "Saving..." : "Publish"}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-foreground font-semibold">Preview</h4>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-muted-foreground bg-muted rounded-lg hover:bg-accent"
                >
                  <EyeOff size={16} /> Edit
                </button>
              </div>

              <div>
                <div className="mb-2">
                  <div className="text-sm text-muted-foreground">Title</div>
                  <div className="text-foreground font-medium">
                    {title || "Untitled"}
                  </div>
                </div>
                <div className="mb-2">
                  <div className="text-sm text-muted-foreground">Audience</div>
                  <div className="text-foreground font-medium">
                    {audience.length
                      ? audience
                          .map((a) => a.charAt(0) + a.slice(1).toLowerCase())
                          .join(", ")
                      : "-"}
                  </div>
                </div>
                <div className="prose max-w-none">
                  <div
                    className="text-muted-foreground"
                    dangerouslySetInnerHTML={{
                      __html: processYouTubeContent(description || ""),
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end pt-2">
                <button
                  onClick={handleSubmit as any}
                  disabled={
                    !title.trim() ||
                    !audience ||
                    !description.trim() ||
                    overLimit ||
                    isSubmitting
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#d7785b] text-white rounded-lg disabled:opacity-60 hover:bg-[#c66a4f]"
                >
                  <Save size={16} /> {isSubmitting ? "Saving..." : "Publish"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
